import { useClickAway } from '@uidotdev/usehooks'
import { useState } from 'react'
import { Rnd } from 'react-rnd'
import {
  cn,
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Badge,
} from 'ui'
import { PDField, usePDFStore } from '../../../stores/pdf.store'
import { getColorHex } from '../../../utils/getColorHex'
import { FIELDS_SETTINGS_PANEL_ID } from '../../Canvas/FieldSettingsPanel'
import { ResizeHandler } from '../../Canvas/FieldsEditLayer/EditableField'
import {
  FieldItem,
  FieldItemLabel,
  FieldItemIcon,
} from '../../Canvas/FieldsEditLayer/FieldItem'
import { buildPagePortalId } from '../../PDFViewer'
import { TunnelRat } from '../../TunnelRat'
import { shadeColor } from '../../../utils/shadeColor'
import { roundDecimal } from '@autosign/utils'
import { findBoundaryFromField } from '../../../utils/findBoundaryFromField'

export const SubmissionFormFieldLabel = ({
  children,
}: {
  children: React.ReactNode
}) => {
  return <p className='font-medium'>{children}</p>
}

export const FormField = ({
  field,
  index,
}: {
  field: PDField
  index: number
}) => {
  const pageBoundary = usePDFStore((state) =>
    findBoundaryFromField(state, field)
  )
  const updateField = usePDFStore((state) => state.updateField)
  const updateStore = usePDFStore((state) => state.update)
  const currentFocusFieldId = usePDFStore((state) => state.currentFocusFieldId)

  const isFocus = currentFocusFieldId === field.id

  const color = usePDFStore(
    (state) => state.recipients.find((r) => r.id === field.recipient_id)!.color
  )

  if (!pageBoundary) return null

  return (
    <TunnelRat tunnelId={buildPagePortalId(field.document_id, field.page)}>
      {({ element }) => {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <FieldItem
                  fontColor={field.font_color}
                  fontSize={field.font_size}
                  fontFamily={field.font_family}
                  className={cn('absolute cursor-pointer')}
                  onClick={() => updateStore('currentFocusFieldId', field.id)}
                  style={{
                    opacity: isFocus ? 1 : 0.5,
                    color: field.font_color,
                    background: getColorHex(color),
                    width: roundDecimal(
                      (+field.width / 100) * pageBoundary.width
                    ),
                    height: (+field.height / 100) * pageBoundary.height,
                    position: 'absolute',
                    left: roundDecimal(
                      (+field.positionX / 100) * pageBoundary.width
                    ),
                    top: roundDecimal(
                      (+field.positionY / 100) * pageBoundary.height
                    ),
                  }}
                >
                  <FieldItemLabel
                    className={cn({
                      'font-bold': field.bold,
                      italic: field.italic,
                      underline: field.underline,
                    })}
                  >
                    <FieldItemIcon type={field.type} />
                    {field!.label}
                  </FieldItemLabel>
                </FieldItem>
              </TooltipTrigger>
              <TooltipContent>
                <p>{field.label}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      }}
    </TunnelRat>
  )
}
